<template>
  <el-dialog
    title="批量上传"
    :visible.sync="dialogVisible"
    width="960px"
    top="0"
    custom-class="batch-upload-dialog linkUploadDialog"
    :close-on-click-modal="false"
    :before-close="handleBeforeClose"
    :close-on-press-escape="false"
    :show-close="!hasCloudUploadActivity"
    append-to-body
  >
    <div class="batch-upload">
      <div class="batch-upload__controls">
      <!-- v-show 而不是 v-if：收起时表单校验和清空选择框仍要用到 formRef / uploaderRef -->
      <div v-show="!controlsCollapsed" class="batch-upload__setup">
      <el-form
        ref="formRef"
        :model="formModel"
        :rules="formRules"
        size="small"
        class="upload-form"
      >
        <div class="form-row">
          <el-form-item label="资源类型" prop="selectedType" class="form-item">
            <el-select
              v-model="selectedType"
              popper-class="genericResourceDropdown"
              placeholder="请选择资源类型"
              filterable
              class="form-control"
              @change="handleTypeChange"
            >
              <el-option
                v-for="item in availableTypes"
                :key="item"
                :label="item"
                :value="item"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            label="默认解锁组"
            prop="defaultLockGroupId"
            class="form-item"
          >
            <el-select
              v-model="defaultLockGroupId"
              popper-class="genericResourceDropdown"
              placeholder="请选择默认解锁组"
              filterable
              clearable
              class="form-control"
            >
              <el-option
                v-for="item in lockGroupOptions"
                :key="item.id"
                :label="item.groupName"
                :value="item.id"
              ></el-option>
            </el-select>
          </el-form-item>
        </div>
      </el-form>

      <p class="linkUploadDialog__tip form-tip" v-if="selectedType">
        {{ zipPackageTip }}
      </p>

      <div
        class="batch-uploader-shell linkUploadDialog__dropzone"
        @click.capture="handleUploaderBlocked"
        @dragover.prevent="handleUploaderBlocked"
        @drop.prevent="handleUploaderBlocked"
      >
        <el-upload
          ref="uploaderRef"
          class="batch-uploader"
          drag
          action=""
          multiple
          :auto-upload="false"
          :show-file-list="false"
          :disabled="!canPickFile"
          :on-change="onFileChange"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击选择</em></div>
        </el-upload>
      </div>
      </div>

      <div class="setup-toggle-row">
        <span
          class="setup-toggle-row__text"
          :title="controlsCollapsed ? collapsedSummary : uploadBlockedText"
          >{{ controlsCollapsed ? collapsedSummary : uploadBlockedText }}</span
        >
        <button
          type="button"
          class="setup-toggle"
          :class="{ 'is-collapsed': controlsCollapsed }"
          :aria-expanded="String(!controlsCollapsed)"
          @click="controlsCollapsed = !controlsCollapsed"
        >
          <span>{{ controlsCollapsed ? '展开设置' : '收起设置' }}</span>
          <i class="el-icon-arrow-up setup-toggle__icon"></i>
        </button>
        <div v-if="acceptsDesigner" class="setup-designer">
          <span class="setup-designer__label">设计师</span>
          <el-select
            v-model="designerId"
            size="mini"
            clearable
            filterable
            placeholder="可以不选"
            popper-class="genericResourceDropdown"
            class="setup-designer__select"
            :disabled="designerLocked"
          >
            <el-option
              v-for="item in designerOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </div>
      </div>

      <input
        ref="reuploadInputRef"
        type="file"
        class="hidden-file-input"
        @change="onReuploadFileSelected"
      />

      <div class="stat-bar">
        <div class="stat-items">
          <div class="stat-chip">
            <span class="stat-label">列表</span>
            <span class="stat-value">{{ fileList.length }}</span>
          </div>
          <div class="stat-chip stat-chip--primary">
            <span class="stat-label">待处理</span>
            <span class="stat-value">{{ statActive }}</span>
          </div>
          <div class="stat-chip stat-chip--success">
            <span class="stat-label">校验通过</span>
            <span class="stat-value">{{ statPassed }}</span>
          </div>
          <div class="stat-chip">
            <span class="stat-label">待上云</span>
            <span class="stat-value">{{ statPendingCloud }}</span>
          </div>
          <div class="stat-chip stat-chip--success">
            <span class="stat-label">上云成功</span>
            <span class="stat-value">{{ statCloudSuccess }}</span>
          </div>
          <div class="stat-chip stat-chip--success">
            <span class="stat-label">落库成功</span>
            <span class="stat-value">{{ statSaveSuccess }}</span>
          </div>
          <div
            class="stat-chip"
            :class="{ 'stat-chip--danger': statFailedRows > 0 }"
          >
            <span class="stat-label">失败</span>
            <span class="stat-value">{{ statFailedRows }}</span>
          </div>
        </div>
        <div class="stat-actions">
          <el-checkbox
            v-model="onlyFailed"
            size="mini"
            :disabled="!onlyFailed && statFailedRows === 0"
            >只看失败</el-checkbox
          >
          <el-button
            v-if="statSaveSuccess > 0"
            type="text"
            size="mini"
            class="clear-completed-btn"
            @click="clearCompletedRows"
            >清空已完成</el-button
          >
        </div>
      </div>
      </div>

      <div class="batch-upload__list">
      <el-table
        ref="fileTableRef"
        class="file-table"
        :data="sortedFileList"
        size="mini"
        height="100%"
        border
        row-key="id"
      >
        <el-table-column
          prop="name"
          label="文件名"
          min-width="140"
          show-overflow-tooltip
        />
        <el-table-column
          v-if="hasBatchRows"
          label="来源批次"
          min-width="120"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">{{ row.batchName || '—' }}</template>
        </el-table-column>
        <el-table-column label="前端校验" width="130">
          <template slot-scope="{ row }">
            <el-tag :type="validateTagType(row)" size="mini">
              {{ validateStatusText(row) }}
            </el-tag>
            <el-tooltip
              v-if="row.validateStatus === 'failed' && row.validateMessage"
              :content="row.validateMessage"
              placement="top"
              :open-delay="300"
            >
              <div class="error-msg">{{ row.validateMessage }}</div>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="上云状态" width="150">
          <template slot-scope="{ row }">
            <el-tag :type="cloudTagType(row)" size="mini">
              {{ cloudStatusText(row) }}
            </el-tag>
            <el-progress
              v-if="row.cloudStatus === 'uploading'"
              :percentage="row.cloudProgress || 0"
              :show-text="false"
              style="margin-top: 4px"
            />
            <el-tooltip
              v-if="row.cloudStatus === 'failed' && row.cloudErrorMessage"
              :content="row.cloudErrorMessage"
              placement="top"
              :open-delay="300"
            >
              <div class="error-msg">{{ row.cloudErrorMessage }}</div>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="落库状态" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="saveTagType(row)" size="mini">
              {{ saveStatusText(row) }}
            </el-tag>
            <el-tooltip
              v-if="row.saveStatus === 'failed' && row.saveErrorMessage"
              :content="row.saveErrorMessage"
              placement="top"
              :open-delay="300"
            >
              <div class="error-msg">{{ row.saveErrorMessage }}</div>
            </el-tooltip>
          </template>
        </el-table-column>
        <!-- 平时只有「删除」或「复制链接」；已上云未落库时最多三个，放不下就折到第二行，不为少数情况撑宽整列 -->
        <el-table-column label="操作" width="132" fixed="right" class-name="op-column">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.publicUrl"
              type="text"
              size="mini"
              @click="copyUrl(row.publicUrl)"
              >复制链接</el-button
            >
            <el-button
              v-if="row.cloudStatus === 'uploading'"
              type="text"
              size="mini"
              @click="cancelUpload(row)"
              >取消</el-button
            >
            <el-tooltip
              v-if="canReuploadRow(row)"
              content="选择新文件替换当前行，可使用不同文件名"
              placement="top"
              :open-delay="300"
            >
              <el-button
                type="text"
                size="mini"
                @click="triggerReupload(row)"
                >重新上传</el-button
              >
            </el-tooltip>
            <el-button
              v-if="canRetrySaveRow(row)"
              type="text"
              size="mini"
              @click="retrySaveRow(row)"
              >重试落库</el-button
            >
            <el-button
              v-if="canRemoveRow(row)"
              type="text"
              size="mini"
              style="color: #f56c6c"
              @click="removeRow(row)"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button
        v-if="!hasCloudUploadActivity"
        size="small"
        type="primary"
        :disabled="!canStartCloud"
        @click="handleStartCloud"
        >开始上云</el-button
      >
      <el-button
        v-else
        size="small"
        type="warning"
        @click="handleCancelCloudBatch"
        >取消上云</el-button
      >
      <el-button
        v-if="!saving"
        size="small"
        :disabled="!canRetrySave"
        @click="handleRetrySave"
        >重试落库</el-button
      >
      <el-button
        v-else
        size="small"
        type="warning"
        :loading="saveBatchAborted"
        :disabled="saveBatchAborted"
        @click="handleCancelSaveBatch"
        >{{ saveBatchCancelButtonText }}</el-button
      >
      <el-button
        size="small"
        :disabled="saving || hasCloudUploadActivity || expandingCount > 0"
        @click="handleCloseClick"
        >关闭</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import { getLockGroupList } from '../../../api/unlockGroup'
import { getAllDesignerOptionCreate } from '../../../api/comment'
import {
  BATCH_OPEN_CONCURRENCY,
  NON_FILE_TYPES,
  UPLOAD_CONCURRENCY,
  VALIDATE_CONCURRENCY
} from './batchUpload/constants'
import { inspectBatchZip, openBatchPack } from './batchUpload/batchZip'
import { uploadRowToCloud } from './batchUpload/cloudUpload'
import {
  applyNewRawToRow,
  buildFileRowId,
  createFileRow,
  isSameFileContent,
  revokeRowPreview
} from './batchUpload/fileRowFactory'
import {
  discardUnsavedCloudObject,
  discardUnsavedCloudObjects,
  saveRowToBackend
} from './batchUpload/resourceSave'
import { createLimiter, runWithLimit } from './batchUpload/runWithLimit'
import { isFailedRow, sortFileList } from './batchUpload/sortFileList'
import { batchUnlockDefault } from './freeUnlockGroup'
import {
  isZipPackageUploadType as checkZipPackageUploadType,
  validateResourceFile
} from './batchUpload/zipValidator'
import {
  resolveRequiredPatterns,
  resolveZipPackageSpec
} from './form/genericResourceZipPackageSpec'

export default {
  name: 'BatchUploadDialog',
  props: {
    visible: { type: Boolean, default: false },
    typeOptions: { type: Array, default: () => [] }
  },
  data() {
    return {
      formModel: {
        selectedType: '',
        defaultLockGroupId: ''
      },
      formRules: {
        selectedType: [
          { required: true, message: '请选择资源类型', trigger: 'change' }
        ],
        defaultLockGroupId: [
          { required: true, message: '请选择默认解锁组', trigger: 'change' }
        ]
      },
      lockGroupOptions: [],
      /** 当前默认解锁组是不是系统按 stickers 自动填的；运营手动选过就不再替他改 */
      lockGroupAutoFilled: false,
      fileList: [],
      uid: 0,
      saving: false,
      cloudBatchRunning: false,
      /** 用户点击「取消上云」后置位，worker 跳过 queued 并配合 abort 停止批次 */
      cloudBatchAborted: false,
      /** 用户点击「取消落库」后置位：跳过未开始行；在途 POST 无法撤回 */
      saveBatchAborted: false,
      lastBlockedHintAt: 0,
      reuploadTargetId: null,
      previousSelectedType: '',
      /** 本会话已成功落库的指纹，防止连点重复提交 */
      savedFingerprints: new Set(),
      /** 正在拆的批次 zip 数；拆完前不能开始上云或关闭 */
      expandingCount: 0,
      /** 重置或切换类型时加一，丢弃在这之前开始、还没拆完的批次 */
      expandGeneration: 0,
      onlyFailed: false,
      /** 收起类型、解锁组和拖拽区，把高度让给几百行的列表 */
      controlsCollapsed: false,
      /** 这一批 stickers 统一的设计师，不选就不写作者 */
      designerId: '',
      designerOptions: []
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      }
    },
    selectedType: {
      get() {
        return this.formModel.selectedType
      },
      set(v) {
        this.formModel.selectedType = v
      }
    },
    defaultLockGroupId: {
      get() {
        return this.formModel.defaultLockGroupId
      },
      set(v) {
        // 只有下拉框（运营操作）走这里；自动填写直接改 formModel
        this.formModel.defaultLockGroupId = v
        this.lockGroupAutoFilled = false
      }
    },
    availableTypes() {
      return (this.typeOptions || []).filter(
        (t) => !NON_FILE_TYPES.includes(t)
      )
    },
    isZipPackageUploadType() {
      return checkZipPackageUploadType(this.selectedType)
    },
    /**
     * zip-only：展示 Registry 必传/可选摘要，避免运营误以为「任意格式都能上云」。
     */
    zipPackageTip() {
      const startTip = '选完后点击「开始上云」，上云成功的会自动保存到后端'
      if (!this.isZipPackageUploadType) {
        return `支持多种文件格式；${startTip}`
      }
      const spec = resolveZipPackageSpec(this.selectedType)
      const required = resolveRequiredPatterns(spec).join('、') || '见类型规范'
      const optional = (spec.optional || []).join('、')
      const optionalPart = optional ? `；可选：${optional}` : ''
      const batchPart = this.acceptsBatchZip
        ? '；也可以直接选批次 zip（打包脚本或 Finder 把多个包 zip 压在一起），会自动拆成一个个包'
        : ''
      return `该类型仅支持 .zip；必传：${required}${optionalPart}${batchPart}。${startTip}`
    },
    /** 批次 zip 目前只给 stickers：素材按目录成百上千个包，其他 zip 类型没有这种用法。 */
    acceptsBatchZip() {
      return this.selectedType === 'stickers'
    },
    /** 和单条编辑页一致：通用资源里只有 stickers 有设计师。 */
    acceptsDesigner() {
      return this.selectedType === 'stickers'
    },
    /** 上传和落库进行中不让换：否则同一批前后落库的包会挂到不同设计师名下。 */
    designerLocked() {
      return this.cloudBatchRunning || this.saving || this.hasCloudInProgress
    },
    sortedFileList() {
      const rows = this.onlyFailed
        ? this.fileList.filter((row) => isFailedRow(row))
        : this.fileList
      return sortFileList(rows)
    },
    hasBatchRows() {
      return this.fileList.some((row) => row.batchName)
    },
    /** 收起后仍要看得到选的是什么：类型和解锁组决定了这批文件落库成什么。 */
    collapsedSummary() {
      const lockGroup = this.lockGroupOptions.find(
        (item) => item.id === this.defaultLockGroupId
      )
      const parts = [
        this.selectedType || '未选资源类型',
        lockGroup ? lockGroup.groupName : '未选默认解锁组'
      ]
      return `${parts.join(' · ')}（展开后可修改或继续添加文件）`
    },
    uploadBlockedText() {
      const missing = this.getMissingUploadRequirements().map((i) => i.label)
      if (missing.length) {
        return `请先选择${missing.join('、')}后再选择文件`
      }
      return this.isZipPackageUploadType
        ? '该类型仅支持 .zip 格式'
        : '支持多种文件格式'
    },
    canPickFile() {
      return (
        this.getMissingUploadRequirements().length === 0 &&
        !this.saving &&
        !this.cloudBatchRunning
      )
    },
    hasUploadingCloud() {
      return this.fileList.some((row) => row.cloudStatus === 'uploading')
    },
    hasCloudInProgress() {
      return this.fileList.some((row) =>
        ['queued', 'uploading'].includes(row.cloudStatus)
      )
    },
    /** 上云批次或排队/传输中：此期间禁止关闭弹窗 */
    hasCloudUploadActivity() {
      return this.cloudBatchRunning || this.hasCloudInProgress
    },
    statActive() {
      return this.fileList.filter((r) => r.saveStatus !== 'success').length
    },
    statPassed() {
      return this.fileList.filter((r) => r.validateStatus === 'passed').length
    },
    statPendingCloud() {
      return this.fileList.filter(
        (r) =>
          r.validateStatus === 'passed' &&
          ['idle', 'failed', 'canceled'].includes(r.cloudStatus)
      ).length
    },
    statCloudSuccess() {
      return this.fileList.filter((r) => r.cloudStatus === 'success').length
    },
    statSaveSuccess() {
      return this.fileList.filter((r) => r.saveStatus === 'success').length
    },
    statFailedRows() {
      return this.fileList.filter((r) => isFailedRow(r)).length
    },
    canStartCloud() {
      if (!this.selectedType || !this.defaultLockGroupId) return false
      if (this.cloudBatchRunning || this.saving) return false
      if (this.hasCloudInProgress || this.expandingCount > 0) return false
      return this.fileList.some(
        (r) =>
          r.validateStatus === 'passed' &&
          ['idle', 'failed', 'canceled'].includes(r.cloudStatus)
      )
    },
    /** 上云后自动落库；落库失败或上云中途被取消而没落库的行，在这里补落。 */
    canRetrySave() {
      if (!this.selectedType || !this.defaultLockGroupId) return false
      if (this.saving || this.cloudBatchRunning) return false
      if (this.hasCloudInProgress) return false
      return this.fileList.some((r) => this.isUnsavedCloudRow(r))
    },
    hasUnsavedCloudSuccess() {
      return this.fileList.some(
        (r) => r.cloudStatus === 'success' && r.saveStatus !== 'success'
      )
    },
    /** 离开页面会丢掉的工作：在途的拆包、上云、落库，以及已上云还没落库的文件。 */
    hasPendingWork() {
      return (
        this.expandingCount > 0 ||
        this.hasCloudUploadActivity ||
        this.saving ||
        this.hasUnsavedCloudSuccess
      )
    },
    saveBatchCancelButtonText() {
      return this.saveBatchAborted ? '正在停止…' : '取消落库'
    }
  },
  watch: {
    visible(v) {
      if (v) {
        this.reset()
        return
      }
      this.discardUnsavedRows()
      this.cleanupAllRows()
    },
    'fileList.length'() {
      this.$nextTick(() => {
        const table = this.$refs.fileTableRef
        if (table) table.doLayout()
      })
    }
  },
  created() {
    // 放在 data 外：闸门只是调度器，不需要响应式。
    this.validateQueue = createLimiter(VALIDATE_CONCURRENCY)
    this.loadLockGroupOptions()
    this.loadDesignerOptions()
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.abortAllUploading('组件销毁，上传已取消')
    this.discardUnsavedRows()
    this.cleanupAllRows()
  },
  methods: {
    /** 刷新、关标签页时由浏览器弹确认框；提示文字由浏览器决定，网页改不了。 */
    handleBeforeUnload(event) {
      if (!this.visible || !this.hasPendingWork) return
      event.preventDefault()
      event.returnValue = ''
    },
    getMissingUploadRequirements() {
      return [
        { field: 'selectedType', label: '资源类型', value: this.selectedType },
        {
          field: 'defaultLockGroupId',
          label: '默认解锁组',
          value: this.defaultLockGroupId
        }
      ].filter((item) => !item.value)
    },
    async loadLockGroupOptions() {
      try {
        const res = await getLockGroupList(
          { pageNum: '', pageSize: '', groupName: '', lockGroupType: '' },
          0
        )
        if (res && res.success) {
          this.lockGroupOptions = res.data || []
          this.applyStickerUnlockDefault()
          return
        }
        throw new Error(
          (res && (res.errMessage || res.message)) || '获取解锁组列表失败'
        )
      } catch (e) {
        this.lockGroupOptions = []
        this.$message.error(e && e.message ? e.message : '获取解锁组列表失败')
      }
    },
    applyStickerUnlockDefault() {
      const next = batchUnlockDefault({
        type: this.selectedType,
        currentId: this.formModel.defaultLockGroupId,
        autoFilled: this.lockGroupAutoFilled,
        options: this.lockGroupOptions
      })
      this.formModel.defaultLockGroupId = next.id
      this.lockGroupAutoFilled = next.autoFilled
      if (next.autoFilled) {
        this.$nextTick(() => {
          const formRef = this.$refs.formRef
          if (formRef) formRef.clearValidate(['defaultLockGroupId'])
        })
      }
    },
    async loadDesignerOptions() {
      try {
        const res = await getAllDesignerOptionCreate()
        this.designerOptions = (res && res.data) || []
      } catch (e) {
        // 设计师可以不选：列表拉不到时下拉为空，不挡上传。
        this.designerOptions = []
      }
    },
    cleanupAllRows() {
      this.fileList.forEach((row) => revokeRowPreview(row))
    },
    discardUnsavedRows() {
      discardUnsavedCloudObjects(this.fileList)
    },
    abortAllUploading(message = '已取消上传') {
      this.fileList.forEach((row) => {
        if (row.cancelSource) {
          row.cancelSource.cancel(message)
          row.cancelSource = null
          return
        }
        // 退避 sleep 中 cancelSource 为空，但 cloudStatus 仍为 uploading：直接打标供 uploadRowToCloud 退出
        if (row.cloudStatus === 'uploading') {
          row.cloudStatus = 'canceled'
          row.cloudProgress = 0
          row.cloudErrorMessage = message
        }
      })
    },
    reset() {
      this.abortAllUploading('弹窗已重置，上传已取消')
      this.cleanupAllRows()
      this.formModel = { selectedType: '', defaultLockGroupId: '' }
      this.previousSelectedType = ''
      this.fileList = []
      this.saving = false
      this.cloudBatchRunning = false
      this.cloudBatchAborted = false
      this.saveBatchAborted = false
      this.uid = 0
      this.reuploadTargetId = null
      this.savedFingerprints = new Set()
      this.expandGeneration += 1
      this.onlyFailed = false
      this.controlsCollapsed = false
      this.designerId = ''
      this.lockGroupAutoFilled = false
      this.$nextTick(() => {
        const formRef = this.$refs.formRef
        if (formRef) formRef.clearValidate()
      })
    },
    handleTypeChange(newType) {
      if (!this.fileList.length) {
        this.previousSelectedType = newType
        this.applyStickerUnlockDefault()
        return
      }
      const oldType =
        this.previousSelectedType ||
        (this.fileList[0] && this.fileList[0].resourceType) ||
        newType
      this.$confirm(
        '切换类型将清空当前文件列表并重置默认解锁组，已上云内容不会落库，是否继续？',
        '切换资源类型',
        { type: 'warning' }
      )
        .then(() => {
          this.abortAllUploading('切换类型，上传已取消')
          this.discardUnsavedRows()
          this.cleanupAllRows()
          this.fileList = []
          this.previousSelectedType = newType
          this.formModel.defaultLockGroupId = ''
          this.lockGroupAutoFilled = false
          this.savedFingerprints = new Set()
          this.expandGeneration += 1
          this.onlyFailed = false
          this.designerId = ''
          this.applyStickerUnlockDefault()
          this.$nextTick(() => {
            const formRef = this.$refs.formRef
            if (formRef) {
              formRef.clearValidate(['defaultLockGroupId'])
            }
          })
        })
        .catch(() => {
          this.selectedType = oldType
        })
    },
    handleUploaderBlocked() {
      if (this.canPickFile) return
      this.showUploadBlockedMessage()
    },
    showUploadBlockedMessage(force = false) {
      const now = Date.now()
      if (!force && now - this.lastBlockedHintAt < 1000) return
      this.lastBlockedHintAt = now
      const formRef = this.$refs.formRef
      if (formRef) {
        this.getMissingUploadRequirements().forEach((item) => {
          formRef.validateField(item.field, () => {})
        })
      }
      this.$message.info(this.uploadBlockedText)
    },
    clearUploaderInput() {
      this.$nextTick(() => {
        const uploader = this.$refs.uploaderRef
        if (uploader && typeof uploader.clearFiles === 'function') {
          uploader.clearFiles()
        }
      })
    },
    rowNeedsFileReplace(row) {
      return (
        row.validateStatus === 'failed' ||
        ['failed', 'canceled'].includes(row.cloudStatus) ||
        row.saveStatus === 'failed'
      )
    },
    onFileChange(uploadFile) {
      if (!this.canPickFile) {
        this.showUploadBlockedMessage(true)
        this.clearUploaderInput()
        return
      }
      const raw = uploadFile.raw
      this.clearUploaderInput()
      if (!raw) {
        return
      }
      if (this.acceptsBatchZip && /\.zip$/i.test(raw.name)) {
        this.addZipOrBatch(raw)
        return
      }
      this.addLocalFile(raw)
    },
    /** 先看是不是批次：是就拆成一个个包加进列表，不是就当单个包。 */
    async addZipOrBatch(raw) {
      const generation = this.expandGeneration
      const resourceType = this.selectedType
      this.expandingCount += 1
      try {
        const result = await inspectBatchZip(raw)
        if (generation !== this.expandGeneration) return
        if (result.kind === 'pack') {
          this.addLocalFile(raw)
          return
        }
        if (result.kind === 'error') {
          this.$message.error(`${raw.name}：${result.message}`)
          return
        }
        await this.addBatchPacks(raw, result, { generation, resourceType })
      } finally {
        this.expandingCount -= 1
      }
    },
    async addBatchPacks(raw, batch, { generation, resourceType }) {
      const skipped = [
        ...batch.ignored.map((name) => `${name}（不是 zip）`),
        ...batch.duplicates.map((name) => `${name}（批次里重名）`)
      ]
      const arrivedAt = Date.now()
      let added = 0
      await runWithLimit(
        batch.packs.map((pack, index) => ({ pack, index })),
        async ({ pack, index }) => {
          let file
          try {
            file = await openBatchPack(raw, pack)
          } catch (error) {
            skipped.push(`${pack.name}（${error.message}）`)
            return
          }
          if (generation !== this.expandGeneration) return
          // 已在列表里的同名包不替换：两个批次撞名多半是两套素材，替换会悄悄丢掉一个。
          if (this.fileList.some((row) => row.id === buildFileRowId(resourceType, file.name))) {
            skipped.push(`${file.name}（列表里已有同名）`)
            return
          }
          const row = createFileRow({ raw: file, resourceType, uid: ++this.uid, batchName: raw.name })
          // 列表按加入时间排，按包在批次里的顺序错开，拆完后和批次里的顺序一致。
          row.addedAt = arrivedAt + index
          this.fileList.push(row)
          added += 1
          this.runRowValidation(row)
        },
        BATCH_OPEN_CONCURRENCY
      )
      if (generation !== this.expandGeneration) return
      this.previousSelectedType = resourceType
      if (added > 0) this.controlsCollapsed = true
      const summary = `「${raw.name}」拆出 ${added} 个包`
      if (!skipped.length) {
        this.$message.success(summary)
        return
      }
      const shown = skipped.slice(0, 5).join('、')
      const more = skipped.length > 5 ? ` 等 ${skipped.length} 项` : ''
      this.$message({
        type: 'warning',
        message: `${summary}，跳过：${shown}${more}`,
        duration: 8000,
        showClose: true
      })
    },
    addLocalFile(raw) {
      const rowId = buildFileRowId(this.selectedType, raw.name)
      const existingIndex = this.fileList.findIndex((r) => r.id === rowId)

      let row
      if (existingIndex >= 0) {
        row = this.fileList[existingIndex]
        if (row.cloudStatus === 'uploading') {
          this.$message.warning('该文件正在上云，请稍候或先取消')
          return
        }
        if (row.saveStatus === 'success') {
          this.$message.warning(
            '该文件已落库成功，请先使用「清空已完成」后再添加'
          )
          return
        }
        if (
          isSameFileContent(row.raw, raw) &&
          !this.rowNeedsFileReplace(row)
        ) {
          this.$message.info('该文件已在列表中，未重复添加')
          return
        }
        discardUnsavedCloudObject(row)
        applyNewRawToRow(row, raw)
      } else {
        row = createFileRow({
          raw,
          resourceType: this.selectedType,
          uid: ++this.uid
        })
        this.fileList.push(row)
      }

      this.previousSelectedType = this.selectedType
      // 文件进了列表，接下来要看的是列表；还要加文件时再点「展开设置」。
      this.controlsCollapsed = true
      this.runRowValidation(row)
    },
    /** 排队校验：轮到之前保持「待校验」；轮到时文件已换或行已删就跳过。 */
    runRowValidation(row) {
      row.validationToken = (row.validationToken || 0) + 1
      const token = row.validationToken
      row.validateStatus = 'pending'
      row.validateMessage = ''
      return this.validateQueue(async () => {
        if (row.validationToken !== token || !this.fileList.includes(row)) return
        row.validateStatus = 'validating'
        let result
        try {
          result = await validateResourceFile(row.raw, row.resourceType)
        } catch (error) {
          result = { ok: false, message: '读取文件失败，请重新选择' }
        }
        if (row.validationToken !== token) return
        if (result.ok) {
          row.validateStatus = 'passed'
          row.validateMessage = ''
        } else {
          row.validateStatus = 'failed'
          row.validateMessage = result.message || '校验失败'
        }
      })
    },
    canReuploadRow(row) {
      if (row.saveStatus === 'success') return false
      if (row.cloudStatus === 'uploading') return false
      return (
        row.validateStatus === 'failed' ||
        ['failed', 'canceled'].includes(row.cloudStatus)
      )
    },
    canRemoveRow(row) {
      if (row.saveStatus === 'success') return false
      if (row.cloudStatus === 'uploading') return false
      if (row.saveStatus === 'saving') return false
      return true
    },
    triggerReupload(row) {
      this.reuploadTargetId = row.id
      const input = this.$refs.reuploadInputRef
      if (!input) return
      input.value = ''
      input.click()
    },
    onReuploadFileSelected(event) {
      const file = event.target.files && event.target.files[0]
      const targetId = this.reuploadTargetId
      event.target.value = ''
      this.reuploadTargetId = null
      if (!file || !targetId) return

      const rowIndex = this.fileList.findIndex((r) => r.id === targetId)
      if (rowIndex < 0) return

      const row = this.fileList[rowIndex]
      const newId = buildFileRowId(row.resourceType, file.name)
      const oldName = row.name

      if (newId !== row.id) {
        const conflictIndex = this.fileList.findIndex((r) => r.id === newId)
        if (conflictIndex >= 0 && conflictIndex !== rowIndex) {
          const conflict = this.fileList[conflictIndex]
          if (conflict.cloudStatus === 'uploading') {
            this.$message.warning('目标文件名正在上云，请稍候或先取消')
            return
          }
          if (conflict.saveStatus === 'success') {
            this.$message.warning(
              '目标文件已落库成功，请先使用「清空已完成」后再操作'
            )
            return
          }
          discardUnsavedCloudObject(conflict)
          applyNewRawToRow(conflict, file)
          discardUnsavedCloudObject(row)
          revokeRowPreview(row)
          this.fileList.splice(rowIndex, 1)
          this.runRowValidation(conflict)
          this.$message.success(
            `已用「${file.name}」替换列表中的同名行，原「${oldName}」行已移除`
          )
          return
        }
        row.id = newId
      }

      discardUnsavedCloudObject(row)
      applyNewRawToRow(row, file)
      this.runRowValidation(row)
      if (file.name !== oldName) {
        this.$message.success(`已替换为「${file.name}」`)
      }
    },
    clearCompletedRows() {
      const completed = this.fileList.filter((r) => r.saveStatus === 'success')
      if (!completed.length) return
      completed.forEach((row) => revokeRowPreview(row))
      this.fileList = this.fileList.filter((r) => r.saveStatus !== 'success')
      this.$message.success(`已清空 ${completed.length} 个落库成功项`)
    },
    async removeRow(row) {
      if (row.cloudStatus === 'success' && row.saveStatus !== 'success') {
        try {
          await this.$confirm(
            '该文件已上云但未落库，删除后不会保存到后端，云端文件和已复制的链接也会失效，是否继续？',
            '确认删除',
            { type: 'warning' }
          )
        } catch (e) {
          return
        }
      }
      if (row.cancelSource) {
        row.cancelSource.cancel('已删除')
        row.cancelSource = null
      }
      discardUnsavedCloudObject(row)
      revokeRowPreview(row)
      const idx = this.fileList.findIndex((r) => r.id === row.id)
      if (idx >= 0) this.fileList.splice(idx, 1)
    },
    async handleStartCloud() {
      if (!this.canStartCloud) return
      if (!(await this.validateForm())) return
      const targets = this.fileList.filter(
        (r) =>
          r.validateStatus === 'passed' &&
          ['idle', 'failed', 'canceled'].includes(r.cloudStatus)
      )
      if (!targets.length) {
        this.$message.warning('没有可上云的文件')
        return
      }

      targets.forEach((row) => {
        row.cloudStatus = 'queued'
        row.cloudRetryCount = 0
        row.cloudErrorMessage = ''
      })
      // 上传中设置区本来就改不了，收起把高度让给进度。
      this.controlsCollapsed = true

      this.cloudBatchAborted = false
      this.cloudBatchRunning = true
      let cloudBatchCanceled = false
      try {
        await runWithLimit(
          targets,
          async (row) => {
            if (this.cloudBatchAborted) {
              if (row.cloudStatus === 'queued') {
                row.cloudStatus = 'canceled'
                row.cloudProgress = 0
                row.cloudErrorMessage = '已取消上传'
              }
              return
            }
            await uploadRowToCloud(row)
            // 传完就落库：几百个包时最容易传完就关页面，等手动点保存会整批作废。
            // 取消后不再落库，已上云的行留给「重试落库」或关闭时回收。
            if (row.cloudStatus === 'success' && !this.cloudBatchAborted) {
              await this.saveRow(row)
            }
          },
          UPLOAD_CONCURRENCY
        )
      } finally {
        cloudBatchCanceled = this.cloudBatchAborted
        this.cloudBatchRunning = false
        this.cloudBatchAborted = false
      }

      this.reportCloudBatch(targets, cloudBatchCanceled)
    },
    saveRow(row) {
      return saveRowToBackend(row, {
        assetType: this.selectedType,
        defaultLockGroupId: this.defaultLockGroupId,
        authorId: this.acceptsDesigner ? this.designerId : '',
        savedFingerprints: this.savedFingerprints
      })
    },
    reportCloudBatch(targets, canceled) {
      const saved = targets.filter((r) => r.saveStatus === 'success').length
      const cloudFailed = targets.filter((r) => ['failed', 'canceled'].includes(r.cloudStatus)).length
      const saveFailed = targets.filter((r) => r.saveStatus === 'failed').length
      const unsaved = targets.filter((r) => this.isUnsavedCloudRow(r) && r.saveStatus === 'idle').length
      if (saved > 0) {
        this.$emit('success')
      }
      const parts = [`落库成功 ${saved} 个`]
      if (cloudFailed) parts.push(`上云失败或取消 ${cloudFailed} 个`)
      if (saveFailed) parts.push(`落库失败 ${saveFailed} 个`)
      if (unsaved) parts.push(`已上云未落库 ${unsaved} 个，可点「重试落库」`)
      const message = `${canceled ? '上传已取消' : '上传完成'}：${parts.join('，')}`
      if (!canceled && parts.length === 1) {
        this.$message.success(message)
      } else {
        this.$message({ type: 'warning', message, duration: 8000, showClose: true })
      }
    },
    cancelUpload(row) {
      if (!row || row.cloudStatus !== 'uploading') return
      if (row.cancelSource) {
        row.cancelSource.cancel('已取消上传')
        row.cancelSource = null
        return
      }
      // 退避等待阶段无 CancelToken：标记 canceled，uploadRowToCloud 在 sleep 后会退出
      row.cloudStatus = 'canceled'
      row.cloudProgress = 0
      row.cloudErrorMessage = '已取消上传'
    },
    /** 取消整批上云：中断传输中的行，并将 queued 标记为已取消 */
    handleCancelCloudBatch() {
      if (!this.hasCloudUploadActivity) return
      this.cloudBatchAborted = true
      this.abortAllUploading('已取消上传')
      this.fileList.forEach((row) => {
        if (row.cloudStatus === 'queued') {
          row.cloudStatus = 'canceled'
          row.cloudProgress = 0
          row.cloudErrorMessage = '已取消上传'
        }
      })
    },
    /** 取消落库：跳过未开始行；在途请求无法中断，完成后以表格为准 */
    handleCancelSaveBatch() {
      if (!this.saving || this.saveBatchAborted) return
      this.saveBatchAborted = true
      const inFlightCount = this.fileList.filter(
        (r) => r.saveStatus === 'saving'
      ).length
      if (inFlightCount > 0) {
        this.$message.warning(
          `正在停止剩余落库：${inFlightCount} 个请求已在途中，若后端返回成功将无法撤回，请稍候对照表格状态。`
        )
      } else {
        this.$message.info('正在停止剩余落库，尚未开始的文件不会提交。')
      }
    },
    async handleRetrySave() {
      if (this.saving) return
      if (!this.canRetrySave) return

      this.saving = true
      this.saveBatchAborted = false
      try {
        if (!(await this.validateForm())) return
        const targets = this.fileList.filter((r) => this.isUnsavedCloudRow(r))
        await this.batchSaveRows(targets)
      } finally {
        this.saving = false
        this.saveBatchAborted = false
      }
    },
    isUnsavedCloudRow(row) {
      return row.cloudStatus === 'success' && ['idle', 'failed'].includes(row.saveStatus)
    },
    canRetrySaveRow(row) {
      return this.isUnsavedCloudRow(row) && !this.saving && !this.cloudBatchRunning
    },
    async retrySaveRow(row) {
      if (!this.canRetrySaveRow(row)) return

      this.saving = true
      this.saveBatchAborted = false
      try {
        await this.batchSaveRows([row])
      } finally {
        this.saving = false
        this.saveBatchAborted = false
      }
    },
    async batchSaveRows(targets) {
      if (!targets.length) return

      let successCount = 0
      let dedupedCount = 0
      let skippedByCancel = 0
      let failCountDuringBatch = 0
      await runWithLimit(
        targets,
        async (row) => {
          if (this.saveBatchAborted) {
            if (row.saveStatus !== 'saving') {
              skippedByCancel += 1
            }
            return
          }
          const result = await this.saveRow(row)
          if (result && result.ok && result.deduped) {
            dedupedCount += 1
          } else if (result && result.ok) {
            successCount += 1
          } else if (result && !result.skipped) {
            failCountDuringBatch += 1
          }
        },
        UPLOAD_CONCURRENCY
      )

      if (this.saveBatchAborted) {
        this.reportSaveBatchCanceled({
          successCount,
          dedupedCount,
          skippedByCancel,
          failCountDuringBatch
        })
        return
      }

      const failCount = targets.length - successCount - dedupedCount
      if (failCount === 0 && successCount === 0 && dedupedCount === 0) {
        return
      }
      if (failCount === 0 && dedupedCount === 0) {
        this.$message.success(`落库成功 ${successCount} 个`)
      } else if (failCount === 0 && successCount === 0) {
        this.$message.info('这些文件已落库，未重复提交')
      } else {
        this.$message.warning(
          `落库完成：成功 ${successCount} 个，失败 ${failCount} 个` +
            (dedupedCount ? `，跳过重复 ${dedupedCount} 个` : '')
        )
      }
      if (successCount > 0) {
        this.$emit('success')
      }
    },
    /** 落库批次被用户取消后的汇总提示：在途成功无法撤回，以表格为准 */
    reportSaveBatchCanceled({
      successCount,
      dedupedCount,
      skippedByCancel,
      failCountDuringBatch
    }) {
      const completedCount = successCount + dedupedCount
      const segments = ['已停止剩余落库']
      if (completedCount > 0) {
        let doneText = `${completedCount} 个在途中已落库（无法撤回）`
        if (dedupedCount > 0) {
          doneText += `，其中 ${dedupedCount} 个为重复跳过`
        }
        segments.push(doneText)
      }
      if (skippedByCancel > 0) {
        segments.push(`${skippedByCancel} 个未开始已跳过`)
      }
      if (failCountDuringBatch > 0) {
        segments.push(`${failCountDuringBatch} 个落库失败`)
      }
      segments.push('请对照表格落库状态')
      const message = `${segments.join('。')}。`

      if (completedCount > 0 || failCountDuringBatch > 0) {
        this.$message.warning(message)
      } else {
        this.$message.info(message)
      }
      if (completedCount > 0) {
        this.$emit('success')
      }
    },
    validateForm() {
      return new Promise((resolve) => {
        const formRef = this.$refs.formRef
        if (!formRef) {
          resolve(true)
          return
        }
        formRef.validate((valid) => resolve(valid))
      })
    },
    validateStatusText(row) {
      const map = {
        pending: '待校验',
        validating: '校验中',
        passed: '通过',
        failed: '失败'
      }
      return map[row.validateStatus] || row.validateStatus
    },
    cloudStatusText(row) {
      if (row.cloudStatus === 'uploading' && row.cloudRetryCount > 0) {
        return `上传中(重试 ${row.cloudRetryCount}/3)`
      }
      const map = {
        idle: '待上云',
        queued: '排队中',
        uploading: '上传中',
        success: '成功',
        failed: '失败',
        canceled: '已取消'
      }
      return map[row.cloudStatus] || row.cloudStatus
    },
    saveStatusText(row) {
      const map = {
        idle: '未落库',
        saving: '落库中',
        success: '成功',
        failed: '失败'
      }
      return map[row.saveStatus] || row.saveStatus
    },
    validateTagType(row) {
      if (row.validateStatus === 'passed') return 'success'
      if (row.validateStatus === 'failed') return 'danger'
      return 'info'
    },
    cloudTagType(row) {
      if (row.cloudStatus === 'success') return 'success'
      if (row.cloudStatus === 'failed') return 'danger'
      if (row.cloudStatus === 'canceled') return 'info'
      if (['uploading', 'queued'].includes(row.cloudStatus)) return 'warning'
      return 'info'
    },
    saveTagType(row) {
      if (row.saveStatus === 'success') return 'success'
      if (row.saveStatus === 'failed') return 'danger'
      if (row.saveStatus === 'saving') return 'warning'
      return 'info'
    },
    async copyUrl(url) {
      try {
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
          throw new Error('Clipboard API unavailable')
        }
        await navigator.clipboard.writeText(url)
        this.$message.success('链接已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败')
      }
    },
    /** 拆包和上云都还在进行时不让关，返回 true 表示已拦下并提示。 */
    blockCloseIfBusy() {
      if (this.hasCloudUploadActivity) {
        this.$message.warning('上云进行中，请等待完成或先取消上传后再关闭')
        return true
      }
      if (this.expandingCount > 0) {
        this.$message.warning('正在拆批次 zip，请稍候再关闭')
        return true
      }
      return false
    },
    async handleBeforeClose(done) {
      if (this.blockCloseIfBusy()) return
      const confirmed = await this.confirmCloseIfNeeded()
      if (!confirmed) return
      this.abortAllUploading('关闭弹窗，上传已取消')
      this.cleanupAllRows()
      done()
    },
    handleCloseClick() {
      if (this.blockCloseIfBusy()) return
      this.confirmCloseIfNeeded().then((confirmed) => {
        if (!confirmed) return
        this.abortAllUploading('关闭弹窗，上传已取消')
        this.cleanupAllRows()
        this.dialogVisible = false
      })
    },
    async confirmCloseIfNeeded() {
      if (this.saving) {
        try {
          await this.$confirm(
            '落库进行中，关闭可能中断操作，是否仍要关闭？',
            '确认关闭',
            { type: 'warning' }
          )
          return true
        } catch (e) {
          return false
        }
      }
      if (this.hasUnsavedCloudSuccess) {
        try {
          await this.$confirm(
            '有文件已上云但未落库，关闭不会保存到后端，这些云端文件和已复制的链接也会失效，是否继续？',
            '确认关闭',
            { type: 'warning' }
          )
          return true
        } catch (e) {
          return false
        }
      }
      return true
    }
  }
}
</script>

<style scoped>
.batch-upload {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
.batch-upload__controls {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.batch-upload__setup {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.batch-upload__list {
  /* flex 子项定高后，内部 el-table height="100%" 才能吃满并只滚 tbody */
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.batch-upload__list >>> .file-table {
  height: 100%;
}
.batch-upload__setup {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.setup-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.setup-toggle-row__text {
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.setup-toggle-row__btn {
  flex-shrink: 0;
  padding: 0;
}
.upload-form {
  margin-bottom: 4px;
}
.batch-uploader-shell {
  position: relative;
}
.hidden-file-input {
  display: none;
}
/* 左右两列等宽，按钮才落在正中；左边提示再长也只省略，不推动按钮 */
.setup-toggle-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  margin-top: -4px;
}
.setup-toggle-row__text {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  color: var(--link-ink-muted, #909399);
}
.setup-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 14px;
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 999px;
  background: var(--link-card-surface, #faf8f5);
  color: var(--link-ink-secondary, #606266);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(28, 25, 23, 0.06);
  transition: color 0.15s, border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
}
.setup-toggle:hover {
  color: var(--link-accent, #2563eb);
  border-color: var(--link-accent-soft-border, #c9d7f5);
  background: var(--link-accent-soft, rgba(37, 99, 235, 0.06));
  box-shadow: 0 2px 6px rgba(28, 25, 23, 0.08);
}
.setup-toggle:focus-visible {
  outline: 2px solid var(--link-accent-soft-border, #c9d7f5);
  outline-offset: 2px;
}
.setup-toggle__icon {
  font-size: 12px;
  transition: transform 0.2s;
}
.setup-toggle.is-collapsed .setup-toggle__icon {
  transform: rotate(180deg);
}
.setup-designer {
  justify-self: start;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.setup-designer__label {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--link-ink-secondary, #606266);
}
.setup-designer__select {
  width: 180px;
}
.form-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}
.form-item {
  flex: 1;
  margin-bottom: 0;
}
.form-control {
  width: 100%;
}
.form-tip {
  margin: 0;
  text-align: left;
}
.batch-uploader >>> .el-upload,
.batch-uploader >>> .el-upload-dragger {
  width: 100%;
}
.batch-uploader >>> .el-upload-dragger {
  height: 140px;
}
.batch-uploader >>> .el-upload-dragger .el-icon-upload {
  margin: 20px 0 10px;
  font-size: 48px;
  line-height: 1;
}
.stat-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: var(--link-workspace, #f3f0ea);
  border: 1px solid var(--link-line, #eee9e2);
  border-radius: var(--link-radius-sm, 8px);
}
.stat-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.stat-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--link-card-surface, #faf8f5);
  border: 1px solid var(--link-line-strong, #e5dfd6);
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
}
.stat-chip--primary {
  border-color: var(--link-accent-soft-border, #c9d7f5);
  background: var(--link-accent-soft, rgba(37, 99, 235, 0.06));
}
.stat-chip--success {
  border-color: #c2e7b0;
  background: #f0f9eb;
}
.stat-chip--danger {
  border-color: #fbc4c4;
  background: #fef0f0;
}
.stat-chip--danger .stat-value {
  color: #f56c6c;
}
.stat-label {
  color: var(--link-ink-muted, #909399);
}
.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--link-ink, #303133);
}
.stat-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.clear-completed-btn {
  flex-shrink: 0;
  padding: 0 4px;
}
.file-table {
  width: 100%;
}
/* 按钮折行时，第二行开头不该带 Element 默认的左间距 */
.file-table >>> .op-column .el-button {
  margin: 0 8px 0 0;
}
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}
.error-msg {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 12px;
  }
}
</style>

<style lang="css">
/*
 * append-to-body 弹窗在组件外，需非 scoped。
 * Element 默认 margin-top:15vh 会顶偏；wrapper flex 真正居中。
 */
.el-dialog__wrapper:has(> .batch-upload-dialog) {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.batch-upload-dialog.el-dialog {
  display: flex;
  flex-direction: column;
  width: min(960px, calc(100vw - 48px));
  /* 必须是定高：只有 max-height 时列表区没有确定高度，el-table height="100%" 会锁死在首次空表的高度 */
  height: min(860px, calc(100vh - 48px));
  margin: 0 !important;
  border-radius: var(--link-radius-md, 12px);
  overflow: hidden;
  box-shadow:
    var(--link-highlight-top, inset 0 1px 0 rgba(255, 255, 255, 0.65)),
    var(--link-shadow-card, 0 18px 48px rgba(28, 25, 23, 0.14));
}

.batch-upload-dialog .el-dialog__header {
  flex-shrink: 0;
}

.batch-upload-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.batch-upload-dialog .el-dialog__footer {
  flex-shrink: 0;
}

.batch-upload-dialog .linkUploadDialog__tip.form-tip {
  margin-top: 0;
  margin-bottom: 4px;
  text-align: left;
}
</style>
